import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import groupService from '../api/groupService';
import '../styles/CreateGroup.css';

const CreateGroup = () => {
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [adminName, setAdminName] = useState('');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { currentUser } = useAuth();
  const { refreshAdminGroup } = useGroup();
  const navigate = useNavigate();
  
  // Set admin name from current user when component mounts
  useEffect(() => {
    if (currentUser) {
      setAdminName(currentUser.name);
    }
  }, [currentUser]);

  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }

    // Check file size (limit to 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size should be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLogo(reader.result);
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!groupName.trim()) {
      setError('Community name is required');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const groupData = {
        name: groupName,
        description,
        adminName,
        logoFile: logo
      };
      
      const response = await groupService.createGroup(groupData);
      
      console.log('✅ Group created successfully:', response.data);
      
      // Immediately update the admin group context with the new group
      // This prevents the empty dashboard issue by ensuring context is ready
      await refreshAdminGroup();
      
      console.log('✅ Context refreshed, redirecting to success page...');
      
      // Redirect to the success page with the group ID
      navigate(`/group-creation-success/${response.data.id}`);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-group-container">
      <h1 className="create-group-title">Create your Community</h1>
      
      <div className="logo-upload-container">
        <div className="logo-preview">
          {logoPreview ? (
            <img 
              src={logoPreview} 
              alt="Group Logo Preview" 
              className="logo-image"
            />
          ) : (
            <div className="logo-placeholder">
              <span className="logo-icon">+</span>
            </div>
          )}
        </div>
        <label className="logo-upload-label">
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            className="logo-upload-input"
          />
          Add Logo
        </label>
        <p className="logo-requirements">
          Recommended: 300×300px, max 5MB (JPEG, PNG, GIF)
        </p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit} className="create-group-form">
        <div className="form-group">
          <label>Community Name</label>
          <input 
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="form-input"
            required
          />
        </div>
        
        <div className="form-group">
          <label>Description</label>
          <textarea 
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-input description-input"
            placeholder="describe your community in a few words"
            rows={4}
          />
        </div>
        
        <div className="form-group">
          <label>Your Name (Admin)</label>
          <input 
            type="text"
            value={adminName}
            onChange={(e) => setAdminName(e.target.value)}
            className="form-input"
            placeholder="Enter your name"
            required
          />
        </div>
        
        <button 
          type="submit" 
          className="create-group-button"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create'}
        </button>
      </form>
    </div>
  );
};

export default CreateGroup;